import { clsx } from 'clsx'
import { useState, useEffect, useRef } from 'react'
import { Undo2 } from 'lucide-react'
import { useUndoStore } from '@/stores/undoStore'
import { useKeyboardSafeBottom } from '@/hooks/useKeyboardSafeBottom'

export function UndoToast() {
  const currentToast = useUndoStore((s) => s.currentToast)
  const undo = useUndoStore((s) => s.undo)
  const dismissToast = useUndoStore((s) => s.dismissToast)
  const kbOffset = useKeyboardSafeBottom()
  const [isExiting, setIsExiting] = useState(false)
  const startXRef = useRef(0)
  const translateXRef = useRef(0)
  const toastRef = useRef<HTMLDivElement>(null)
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setIsExiting(false)
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current)
    }
  }, [currentToast])

  if (!currentToast) return null

  const handleDismiss = () => {
    setIsExiting(true)
    dismissTimerRef.current = setTimeout(dismissToast, 200)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX
    translateXRef.current = 0
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - startXRef.current
    translateXRef.current = dx
    if (toastRef.current) {
      toastRef.current.style.transform = `translateX(${dx}px)`
      toastRef.current.style.opacity = `${Math.max(0, 1 - Math.abs(dx) / 200)}`
    }
  }

  const handleTouchEnd = () => {
    if (Math.abs(translateXRef.current) > 100) {
      handleDismiss()
    } else if (toastRef.current) {
      toastRef.current.style.transform = ''
      toastRef.current.style.opacity = ''
    }
  }

  return (
    // 외부 래퍼는 위치를 잡지 않는 패스스루 — 위치/safe-area/데스크톱 정렬은 .toast-foundation가 담당한다.
    // (이중 position:fixed 방지: position:fixed 자식은 부모가 아닌 뷰포트에 고정되므로 래퍼의 bottom/offset이 무시됨)
    <div role="status" aria-live="polite">
      <div
        ref={toastRef}
        className={clsx(
          'toast-foundation toast-icon-text-action transition-transform z-50',
          // .toast-enter/.toast-exit는 toast.css에서 데스크톱 translateX(-50%) 중앙정렬을 유지하는 키프레임을 가짐
          isExiting ? 'toast-exit' : 'toast-enter'
        )}
        // 키보드가 떠 있을 때만 .toast-foundation의 bottom을 직접 덮어써 키보드 위로 띄운다.
        style={
          kbOffset > 0
            ? { bottom: `calc(96px + env(safe-area-inset-bottom, 0px) + ${kbOffset}px)` }
            : undefined
        }
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="toast-message">{currentToast.label}</span>
          <button
            type="button"
            onClick={async () => {
              await undo()
            }}
            className="toast-action flex items-center gap-1.5 text-primary-400 hover:text-primary-300"
          >
            <Undo2 className="w-4 h-4" />
            실행 취소
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="toast-close"
          >
            닫기
          </button>
        </div>
        {/* Countdown progress bar */}
        <div className="toast-countdown-track">
          <div className="toast-countdown-bar" />
        </div>
      </div>
    </div>
  )
}
